import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';

import { AppComponent } from 'src/app/app.component';
import { IoService } from '../../services/io/io.service';
import { LocationService } from '../../services/location/location.service';
import { ClientSettingsService } from '../../services/client-settings/client-settings.service';
import { ResponsiveService } from '../../services/responsive/responsive.service';
import { SupportService } from '../../services/support/support.service';
import { SettingsService } from '../../services/settings/settings.service';
import { HeaderService } from '../../services/header/header.service';
import { TagPrintService, TagPrintTemplate } from '../../services/tag-print/tag-print.service';
import { parseEncodedBarcode } from '../../services/barcode-parser/barcode-parser';

// Price Check: the dashboard twin of the register's Price Check menu action.
// One input takes a scanner read (keyboard wedge, Enter-terminated) or typed
// text. A code is looked up on the server (priceCheck/lookup), which finds the
// item the way the register does, resolves the store price and runs the shared
// discount engine for the walk-up price and multi-buy deals. Text that is not
// an item code falls through to a name search; tapping a result shows its card.
// Scans also arrive while nothing is focused (window keyup buffer), so a
// handheld keeps working after the soft keyboard is dismissed.
interface PriceCheckCard {
  _id: string;
  name: string;
  sku: string | null;
  barcode: string[];
  unitDescription: string | null;
  unitOfMeasure: string | null;
  attr: string | null;
  imgUrl: string | null;
  categoryName: string | null;
  price: number;
  priceIsVariable: boolean;
  discountedPrice: number | null;
  discountNames: string[];
  deals: Array<{ qty: number; totalPrice: number; eachPrice: number; names: string[] }>;
  inStock: number | null;
  otherLocations: Array<{ locationId: string; locationName: string; balance: number }>;
}

@Component({
  selector: 'app-price-check',
  templateUrl: './price-check.component.html',
  styleUrls: ['./price-check.component.scss']
})
export class PriceCheckComponent implements OnInit, OnDestroy {

  @ViewChild('entry') entryInput: ElementRef<HTMLInputElement>;

  location: any = null;
  entryText = '';
  busy = false;

  items: PriceCheckCard[] = [];
  lastCode: string | null = null;
  notFound = false;

  searchResults: any[] = [];
  searchText: string | null = null;
  searchNoMatches = false;

  printTemplates: TagPrintTemplate[] = [];
  printing = false;
  isMobile = false;

  // The header's Settings / Support actions render into the current screen
  // (every full-screen route hosts the two panels itself).
  supportActive = false;
  settingsActive = false;

  private locationSub: Subscription;
  private mobileSub: Subscription;
  private supportSub: Subscription;
  private settingsSub: Subscription;
  private keyboardBuffer = '';
  private lastKeyTime = 0;

  constructor(
    private ioService: IoService,
    private locationService: LocationService,
    private clientSettingsService: ClientSettingsService,
    private responsiveService: ResponsiveService,
    private tagPrintService: TagPrintService,
    private supportService: SupportService,
    private settingsService: SettingsService,
    private headerService: HeaderService,
  ) { }

  ngOnInit() {
    this.mobileSub = this.responsiveService.isMobile$.subscribe(mobile => this.isMobile = mobile);
    this.setLocation(this.locationService.getActiveLocation());
    this.locationSub = this.locationService.activeLocationSetObservable().subscribe(location => {
      this.setLocation(location);
    });
    this.clientSettingsService.loadClientSettings().catch(() => null);
    this.supportActive = this.supportService.isSupportActive();
    this.supportSub = this.supportService.supportActive$.subscribe(active => this.supportActive = active);
    this.settingsActive = this.settingsService.isSettingsActive();
    this.settingsSub = this.settingsService.settingsActive$.subscribe(active => this.settingsActive = active);
    setTimeout(() => this.focusEntry(), 200);
  }

  ngOnDestroy() {
    if (this.locationSub) { this.locationSub.unsubscribe(); }
    if (this.mobileSub) { this.mobileSub.unsubscribe(); }
    if (this.supportSub) { this.supportSub.unsubscribe(); }
    if (this.settingsSub) { this.settingsSub.unsubscribe(); }
  }

  // No store selected: a tap on the box (or the prompt) opens the header's
  // location picker rather than doing nothing.
  entryClick() {
    if (!this.location) {
      this.headerService.requestLocationsMenu();
    }
  }

  private setLocation(location: any) {
    const previousId = this.location && this.location._id;
    this.location = (location && location._id) ? location : null;
    if (this.location && this.location._id !== previousId) {
      this.loadPrintTemplates();
      // Prices and deals are per store: re-check what is on screen.
      if (this.items.length > 0) {
        this.lookupByAccountIds(this.items.map(item => item._id));
      }
    }
  }

  // Scanner output while nothing is focused (handheld after the keyboard is
  // dismissed, desktop after a click elsewhere): buffer keys, act on Enter.
  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent) {
    if (event.target.toString() !== '[object HTMLBodyElement]') {
      return;
    }
    const now = Date.now();
    if (now - this.lastKeyTime > 500) {
      this.keyboardBuffer = '';
    }
    this.lastKeyTime = now;
    if (event.key === 'Enter') {
      const code = this.keyboardBuffer;
      this.keyboardBuffer = '';
      if (code.length > 1) {
        if (!this.location) {
          this.headerService.requestLocationsMenu();
          return;
        }
        this.submit(code, true);
      }
      return;
    }
    if (event.key && event.key.length === 1) {
      this.keyboardBuffer += event.key;
    }
  }

  entryKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (!this.location) {
        this.headerService.requestLocationsMenu();
        return;
      }
      const text = this.entryText;
      this.submit(text, false);
    }
  }

  focusEntry() {
    if (this.entryInput && this.entryInput.nativeElement) {
      this.entryInput.nativeElement.focus();
    }
  }

  private afterEntryHandled(fromScanner: boolean) {
    this.entryText = '';
    if (this.isMobile && this.entryInput && this.entryInput.nativeElement) {
      // Drop the soft keyboard so the cards are visible; the wedge keeps working
      // through the window keyup buffer.
      this.entryInput.nativeElement.blur();
    } else if (!fromScanner) {
      this.focusEntry();
    } else {
      this.focusEntry();
    }
  }

  // A scanner read or an Enter in the input: try it as an item code first,
  // then as a name search.
  async submit(raw: string, fromScanner: boolean) {
    const text = (raw || '').trim();
    if (!text || this.busy) { return; }
    if (!this.location) {
      AppComponent.showSnack('Select a location in the header first.', 'OK', 3000);
      return;
    }
    this.busy = true;
    try {
      const decoded = parseEncodedBarcode(text, this.clientSettingsService.getBarcodeParsers());
      const body: any = { locationId: this.location._id, code: decoded ? decoded.itemCode : text };
      if (decoded && decoded.price != null && decoded.price > 0) {
        body.price = decoded.price;
      }
      const response: any = await this.ioService.post('priceCheck/lookup', body);
      if (response && response.found) {
        this.showItems(response.items, text);
        this.afterEntryHandled(fromScanner);
        return;
      }
      // Not an item code: a typed phrase becomes a search; a scanned code stays
      // a not-found scan.
      if (!fromScanner && /[a-zA-Z ]/.test(text)) {
        await this.runSearch(text);
      } else {
        this.items = [];
        this.lastCode = text;
        this.notFound = true;
        this.clearSearch();
      }
      this.afterEntryHandled(fromScanner);
    } catch (err) {
      AppComponent.showSnack(err?.error?.msg || 'Price check failed.', 'OK', 4000);
    } finally {
      this.busy = false;
    }
  }

  private showItems(items: PriceCheckCard[], code: string | null) {
    this.items = items || [];
    this.lastCode = code;
    this.notFound = false;
    this.clearSearch();
  }

  private async lookupByAccountIds(accountIds: string[]) {
    const cards: PriceCheckCard[] = [];
    for (const accountId of accountIds) {
      try {
        const response: any = await this.ioService.post('priceCheck/lookup', { locationId: this.location._id, accountId });
        if (response && response.found) { cards.push(...response.items); }
      } catch (e) { }
    }
    this.items = cards;
  }

  async runSearch(text: string) {
    this.searchText = text;
    this.items = [];
    this.notFound = false;
    const response: any = await this.ioService.post('priceCheck/search', { locationId: this.location._id, search: text });
    this.searchResults = (response && response.items) || [];
    this.searchNoMatches = this.searchResults.length === 0;
  }

  clearSearch() {
    this.searchText = null;
    this.searchResults = [];
    this.searchNoMatches = false;
  }

  async selectSearchResult(result: any) {
    if (this.busy) { return; }
    this.busy = true;
    try {
      const response: any = await this.ioService.post('priceCheck/lookup', { locationId: this.location._id, accountId: result._id });
      if (response && response.found) {
        this.showItems(response.items, null);
      }
    } catch (err) {
      AppComponent.showSnack(err?.error?.msg || 'Price check failed.', 'OK', 4000);
    } finally {
      this.busy = false;
    }
  }

  resultDetail(item: any): string {
    return [item.unitDescription, item.attr].filter(part => part != null && String(part).trim() !== '').join('  ');
  }

  private async loadPrintTemplates() {
    try {
      const templates: any = await this.ioService.post('priceCheck/getPrintTemplates', { locationId: this.location._id });
      this.printTemplates = Array.isArray(templates) ? templates : [];
    } catch (e) {
      this.printTemplates = [];
    }
  }

  async printItem(item: PriceCheckCard, template: TagPrintTemplate) {
    if (this.printing) { return; }
    this.printing = true;
    try {
      await this.tagPrintService.printInventoryItem(item._id, template.key, this.location ? this.location._id : null, item.name);
    } catch (err) {
      AppComponent.showSnack(err?.error?.msg || err?.message || ('Unable to print ' + template.name + '.'), 'OK', 4000);
    } finally {
      this.printing = false;
    }
  }

  clear() {
    this.items = [];
    this.lastCode = null;
    this.notFound = false;
    this.clearSearch();
    this.entryText = '';
    this.focusEntry();
  }
}
